import { type CrewAssignment, servingOn } from './crew.js';
import { addDays, addMonths, daysBetween, type LocalDate } from './local-date.js';

/**
 * Kontrat bitişi ve MLC hizmet sınırı (ADR 0076). Saf kural: `today` parametreyle gelir; bütün
 * tarihler gemi yerel tarihidir (`YYYY-MM-DD`, CLAUDE.md "Zaman").
 *
 * Gemide kesintisiz hizmet 12 aydan kısa olmalıdır: "the maximum duration of service periods on
 * board following which a seafarer is entitled to repatriation – such periods to be less than 12
 * months" [MLC 2006 Std A2.5.1 §2(b); Guideline B2.5.1 §8]. Basit kural: katılış + 12 ay − 1 gün
 * son gündür (izin hesabı yapılmaz).
 */
export const MLC_MAX_SERVICE_MONTHS = 12;
/** Uyarı eşiği: sınıra bu kadar gün (dahil) kala "warning" (sahip isteği 2026-10-17: "2 ay kala"). */
export const CONTRACT_WARNING_DAYS = 60;

/** Gemi ataması ve şirketin girdiği kontrat bitişi (son gün dahil). */
export interface ContractAssignment extends CrewAssignment {
  contractEnd?: string | null;
}

export type ContractState = 'ok' | 'warning' | 'overdue';

export interface CrewContract {
  /** Kesintisiz gemi hizmetinin başlangıcı (katılış). */
  joinedOn: LocalDate;
  contractEnd: LocalDate | null;
  /** MLC'ye göre gemide son gün (katılış + 12 ay − 1 gün). */
  mlcLimit: LocalDate;
  /** Uyarı tarihi: min(kontrat bitişi, MLC sınırı). */
  dueOn: LocalDate;
  /** Uyarı tarihini hangisi belirliyor. */
  basis: 'contract' | 'mlc';
  /** `dueOn - today`; 0 bugün son gün, eksi geçmiş. */
  daysLeft: number;
  state: ContractState;
  /** Kontrat bitişi MLC sınırını aşıyor (girişte gerekçe istenir). */
  exceedsMlc: boolean;
}

/** MLC'ye göre gemide son gün: katılış + 12 ay − 1 gün ("less than 12 months"). */
export function mlcServiceLimit(joinedOn: LocalDate): LocalDate {
  return addDays(addMonths(joinedOn, MLC_MAX_SERVICE_MONTHS), -1);
}

/** Kontrat bitişi MLC sınırından sonra mı. */
export function contractExceedsMlc(joinedOn: LocalDate, contractEnd: LocalDate): boolean {
  return contractEnd > mlcServiceLimit(joinedOn);
}

/** Kalan güne göre durum: ≤ 60 gün (0 dahil) warning, geçtiyse overdue. */
export function contractState(daysLeft: number): ContractState {
  if (daysLeft < 0) return 'overdue';
  return daysLeft <= CONTRACT_WARNING_DAYS ? 'warning' : 'ok';
}

/**
 * `index`'teki görevin dahil olduğu kesintisiz hizmetin başlangıcı: bir önceki gün ya da daha
 * geç biten (ardışık ya da örtüşen) görevler, gemi değişse de aynı hizmettir; tek günlük boşluk
 * zinciri keser (kişi karadadır).
 */
export function continuousServiceStart(
  assignments: readonly CrewAssignment[],
  index: number,
): LocalDate {
  const row = assignments[index];
  if (!row) throw new RangeError(`assignment ${index}`);
  let start = row.validFrom;
  for (;;) {
    const before = addDays(start, -1);
    const earlier = assignments
      .filter((item) => item.validFrom < start && (item.validTo === null || item.validTo >= before))
      .map((item) => item.validFrom)
      .sort()[0];
    if (earlier === undefined) return start;
    start = earlier;
  }
}

/**
 * Bugün gemide olan kişinin kontrat durumu; karadaysa `null`. Kontrat bitişi güncel görevden,
 * o görevde yoksa aynı hizmetteki en son başlayan ve bitiş girilmiş görevden gelir.
 */
export function crewContract(
  assignments: readonly ContractAssignment[],
  today: LocalDate,
): CrewContract | null {
  const sorted = [...assignments].sort((x, y) => x.validFrom.localeCompare(y.validFrom));
  const index = sorted.findLastIndex((item) => servingOn(item, sorted, today));
  if (index < 0) return null;
  const joinedOn = continuousServiceStart(sorted, index);
  const contractEnd =
    sorted
      .slice(0, index + 1)
      .filter((item) => item.validFrom >= joinedOn && item.contractEnd)
      .at(-1)?.contractEnd ?? null;
  const mlcLimit = mlcServiceLimit(joinedOn);
  const basis = contractEnd !== null && contractEnd <= mlcLimit ? 'contract' : 'mlc';
  const dueOn = basis === 'contract' && contractEnd !== null ? contractEnd : mlcLimit;
  const daysLeft = daysBetween(today, dueOn);
  return {
    joinedOn,
    contractEnd,
    mlcLimit,
    dueOn,
    basis,
    daysLeft,
    state: contractState(daysLeft),
    exceedsMlc: contractEnd !== null && contractEnd > mlcLimit,
  };
}
